/**
 * ActivationGate — mounts the first-run activation wizard for a brand-new org's
 * founder, after OnboardingGate. Renders the wizard IN PLACE of the app (like
 * OnboardingGate) when activation is needed, otherwise passes children through.
 *
 * "Needs activation" = the caller is the org founder (ORG_ADMIN), the wizard hasn't
 * been dismissed/completed, and the org has zero projects (a genuinely new org). A
 * later invitee lands in an org that already has projects, so this is a no-op for them.
 *
 * SOFT gate (Bob-pending decision, default): the wizard is skippable per-step and
 * dismissible. To make it a HARD gate (block the app until a project exists), change
 * `needsActivation` to also require `projectCount === 0` even after dismissal, or gate
 * only the project step — a one-line change kept here on purpose.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { activationApi, EMPTY_ACTIVATION_PROGRESS } from "@/api/activationApi";
import type { ActivationProgress } from "@/api/activationApi";
import { projectsApi } from "@/api/services";
import { useAuthStore } from "@/stores/auth";
import { ActivationWizard } from "./ActivationWizard";

export function ActivationGate({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isFounder = (user?.roles ?? []).some((r) => r.toUpperCase() === "ORG_ADMIN");

  const [checked, setChecked] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);

  useEffect(() => {
    // Only the founder of a brand-new org sees the wizard. Everyone else passes
    // through immediately without an extra round-trip.
    if (!isFounder) {
      setChecked(true);
      return;
    }
    let active = true;
    Promise.all([
      activationApi.getProgress().catch(() => EMPTY_ACTIVATION_PROGRESS as ActivationProgress),
      projectsApi.list({ limit: 1 }).catch(() => ({ total: 1 } as { total: number })),
    ])
      .then(([progress, projects]) => {
        if (!active) return;
        const alreadyHandled = progress.dismissed || !!progress.completed_at;
        const hasProjects = (projects?.total ?? 0) > 0;

        // Step 1 of the wizard CREATES a project — so "has no projects" stops
        // being true the moment the journey starts. Gating on it alone meant
        // the wizard deleted its own precondition: any remount (reload, new
        // tab, next login, a route change that swaps the wrapper) dropped the
        // founder out with steps 2-4 never shown, and `/get-started` is
        // reachable only by typing the URL — grep finds one hit, the route
        // definition, with no link, nav entry or empty-state CTA anywhere.
        // The progress the wizard had been faithfully persisting was
        // unreachable state, because nothing ever read the step map back.
        //
        // So: a journey that has been STARTED but not finished or dismissed
        // resumes, whatever the project count. `hasProjects` now only decides
        // whether to start the journey for someone who never began it.
        const startedButUnfinished = Object.values(progress.steps ?? {}).some(
          (s) => s?.done || s?.skipped,
        );
        setNeedsActivation(!alreadyHandled && (startedButUnfinished || !hasProjects));
      })
      .finally(() => {
        if (active) setChecked(true);
      });
    return () => {
      active = false;
    };
  }, [isFounder]);

  // Until the check resolves, render children (avoid a blocking flash). The wizard
  // only takes over once we know the org is genuinely new + unhandled.
  if (checked && needsActivation) {
    return <ActivationWizard onDone={() => setNeedsActivation(false)} />;
  }
  return <>{children}</>;
}

export default ActivationGate;
